import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { assinaturaApi, planosApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Card, CardHeader, CardBody, ProgressBar } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState, ErrorState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import {
  CicloCobranca, CICLO_LABEL, StatusAssinatura, STATUS_ASSINATURA_LABEL,
  StatusPagamento, STATUS_PAGAMENTO_LABEL,
} from '../../lib/enums'
import { formatMoney, formatDate, formatDateTime, cn } from '../../lib/utils'
import type { Plano, UsoDoPlano, PreviaTroca, TipoHistoricoTroca } from '../../types'

const STATUS_VARIANT: Record<number, 'income' | 'warning' | 'expense' | 'muted'> = {
  [StatusAssinatura.Ativa]: 'income',
  [StatusAssinatura.Trial]: 'warning',
  [StatusAssinatura.Pendente]: 'warning',
  [StatusAssinatura.Inadimplente]: 'expense',
  [StatusAssinatura.Cancelada]: 'muted',
}

const PAGAMENTO_VARIANT: Record<number, 'income' | 'warning' | 'expense' | 'muted'> = {
  [StatusPagamento.Pago]: 'income',
  [StatusPagamento.Pendente]: 'warning',
  [StatusPagamento.Falhou]: 'expense',
  [StatusPagamento.Estornado]: 'muted',
  [StatusPagamento.Cancelado]: 'muted',
}

const TIPO_HISTORICO_LABEL: Record<TipoHistoricoTroca, string> = {
  Upgrade: 'Upgrade',
  Ciclo: 'Troca de ciclo',
  DowngradeAgendado: 'Downgrade agendado',
  DowngradeAplicado: 'Downgrade aplicado',
  TrocaCancelada: 'Troca cancelada',
  TrocaExpirada: 'Troca expirada',
}

const MOTIVOS = [
  'Está caro para mim',
  'Não uso o suficiente',
  'Faltam recursos que eu preciso',
  'Vou usar outro aplicativo',
  'Problemas técnicos',
]

export function PlanoPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [ciclo, setCiclo] = useState<CicloCobranca>(CicloCobranca.Mensal)
  const [confirmar, setConfirmar] = useState<Plano | null>(null)
  const [cancelarAberto, setCancelarAberto] = useState(false)
  const [motivo, setMotivo] = useState(MOTIVOS[0])
  const [detalhe, setDetalhe] = useState('')

  const { data: assinatura, isLoading, isError } = useQuery({ queryKey: ['assinatura'], queryFn: assinaturaApi.minha })
  const { data: planos = [] } = useQuery({ queryKey: ['planos'], queryFn: planosApi.list })
  const { data: pagamentos = [] } = useQuery({ queryKey: ['assinatura-pagamentos'], queryFn: assinaturaApi.pagamentos })
  const { data: historico = [] } = useQuery({ queryKey: ['assinatura-historico'], queryFn: assinaturaApi.historico })

  const previa = useQuery({
    queryKey: ['assinatura-previa', confirmar?.codigo, ciclo],
    queryFn: () => assinaturaApi.previaTroca(confirmar!.codigo, ciclo),
    enabled: !!confirmar,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })

  const assinar = useMutation({
    mutationFn: (plano: Plano) => assinaturaApi.assinar(plano.codigo, ciclo),
    onSuccess: async (_, plano) => {
      qc.invalidateQueries({ queryKey: ['assinatura'] })
      qc.invalidateQueries({ queryKey: ['assinatura-pagamentos'] })
      qc.invalidateQueries({ queryKey: ['assinatura-historico'] })
      const p = previa.data
      setConfirmar(null)
      toast.success(
        p?.cobraAgora ? 'Troca solicitada. Pague para ela valer.'
          : p?.tipo === 'DowngradeAgendado' ? `Troca para ${plano.nome} agendada.`
          : 'Plano alterado.',
      )
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const cancelarTroca = useMutation({
    mutationFn: () => assinaturaApi.cancelarTroca(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assinatura'] })
      qc.invalidateQueries({ queryKey: ['assinatura-pagamentos'] })
      qc.invalidateQueries({ queryKey: ['assinatura-historico'] })
      toast.success('Troca cancelada. Seu plano segue como está.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const cancelar = useMutation({
    mutationFn: () => assinaturaApi.cancelar(detalhe.trim() ? `${motivo} — ${detalhe.trim()}` : motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assinatura'] })
      setCancelarAberto(false)
      toast.success('Assinatura cancelada. Sua conta segue no plano grátis.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const gerarLink = useMutation({
    mutationFn: (pagamentoId: string) => assinaturaApi.gerarLink(pagamentoId),
    onSuccess: (r) => {
      if (r.linkPagamento) {
        window.open(r.linkPagamento, '_blank', 'noopener')
        qc.invalidateQueries({ queryKey: ['assinatura-pagamentos'] })
      } else {
        toast.error(r.aviso ?? 'Não foi possível gerar o link de pagamento.')
      }
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (isLoading) return <><PageHeader title="Meu plano" /><LoadingState /></>
  if (isError || !assinatura) return <><PageHeader title="Meu plano" /><ErrorState message="Erro ao carregar sua assinatura." /></>

  const atual = assinatura.plano
  const trocaPendente = assinatura.trocaPendente ?? null
  const trocaAgendada = assinatura.trocaAgendada ?? null
  const pendente = pagamentos.find((p) =>
    p.status === StatusPagamento.Pendente && !p.planoDestino && p.id !== trocaPendente?.pagamentoId)

  return (
    <>
      <PageHeader title="Meu plano" />

      <div className="space-y-6">
        <Card>
          <CardBody className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-xl font-semibold text-white">{atual.nome}</h4>
                <Badge variant={STATUS_VARIANT[assinatura.status] ?? 'muted'}>{STATUS_ASSINATURA_LABEL[assinatura.status]}</Badge>
                {assinatura.valor > 0 && <Badge variant="muted">{CICLO_LABEL[assinatura.ciclo]}</Badge>}
              </div>
              <p className="mt-1 text-13 text-muted">
                {assinatura.status === StatusAssinatura.Trial && assinatura.diasDeTrialRestantes !== undefined
                  ? `Faltam ${assinatura.diasDeTrialRestantes} dias de teste. Depois disso, a conta volta para o plano grátis se você não assinar.`
                  : assinatura.renovaEm
                    ? `Renova em ${formatDate(assinatura.renovaEm)} por ${formatMoney(assinatura.valor)}.`
                    : 'Plano grátis, sem cobrança.'}
              </p>
            </div>

            {assinatura.valor > 0 && assinatura.status !== StatusAssinatura.Cancelada && (
              <Button variant="soft-danger" icon="ri-close-circle-line" onClick={() => setCancelarAberto(true)}>Cancelar assinatura</Button>
            )}
          </CardBody>

          {trocaPendente && (
            <div className="border-t border-border/60 bg-warning/5 px-4 py-3">
              <div className="flex flex-wrap items-center gap-3">
                <i className="ri-swap-line text-warning" aria-hidden />
                <div className="min-w-0 flex-1 text-13 text-white">
                  <p className="font-medium">Troca pendente para {trocaPendente.planoNovo.nome} ({CICLO_LABEL[trocaPendente.ciclo]})</p>
                  <p className="text-muted">
                    Pague <strong className="text-white">{formatMoney(trocaPendente.valorACobrar)}</strong> até {formatDate(trocaPendente.vencimento)} para a troca valer.
                    Até lá, você continua no {atual.nome}.
                  </p>
                </div>
                <Button size="sm" icon="ri-bank-card-line" loading={gerarLink.isPending} onClick={() => gerarLink.mutate(trocaPendente.pagamentoId)}>
                  Pagar
                </Button>
                <Button size="sm" variant="light" loading={cancelarTroca.isPending} onClick={() => cancelarTroca.mutate()}>
                  Cancelar troca
                </Button>
              </div>
            </div>
          )}

          {trocaAgendada && (
            <div className="flex flex-wrap items-center gap-3 border-t border-border/60 bg-primary/5 px-4 py-3">
              <i className="ri-calendar-event-line text-primary" aria-hidden />
              <span className="flex-1 text-13 text-white">
                Você passará para <strong>{trocaAgendada.planoNovo.nome}</strong> em {formatDate(trocaAgendada.aplicarEm)}.
              </span>
              <Button size="sm" variant="light" loading={cancelarTroca.isPending} onClick={() => cancelarTroca.mutate()}>
                Desfazer
              </Button>
            </div>
          )}

          {pendente && (
            <div className="flex flex-wrap items-center gap-3 border-t border-border/60 bg-warning/5 px-4 py-3">
              <i className="ri-time-line text-warning" aria-hidden />
              <span className="flex-1 text-13 text-white">
                Pagamento de <strong>{formatMoney(pendente.valor)}</strong> em aberto, vence {formatDate(pendente.vencimento)}.
              </span>
              <Button size="sm" icon="ri-bank-card-line" loading={gerarLink.isPending} onClick={() => gerarLink.mutate(pendente.id)}>
                Pagar agora
              </Button>
            </div>
          )}
        </Card>

        <Card>
          <CardHeader title="Uso do plano" icon="ri-bar-chart-grouped-line" />
          <CardBody className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <LinhaDeUso titulo="Contas" usado={assinatura.uso.contas} limite={atual.maxContas} />
            <LinhaDeUso titulo="Cartões" usado={assinatura.uso.cartoes} limite={atual.maxCartoes} />
            <LinhaDeUso titulo="Metas" usado={assinatura.uso.metas} limite={atual.maxMetas} />
            <LinhaDeUso titulo="Orçamentos" usado={assinatura.uso.orcamentos} limite={atual.maxOrcamentos} />
            <LinhaDeUso titulo="Compartilhamentos" usado={assinatura.uso.compartilhamentos} limite={atual.maxCompartilhamentos} />
          </CardBody>
        </Card>

        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h5 className="text-15 font-medium text-white">Planos</h5>
            <div className="inline-flex overflow-hidden rounded bg-surface2">
              {[CicloCobranca.Mensal, CicloCobranca.Anual].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCiclo(c)}
                  className={cn('px-4 py-2 text-13 transition-colors', ciclo === c ? 'bg-primary text-bg font-medium' : 'text-muted hover:text-white')}
                >
                  {CICLO_LABEL[c]}{c === CicloCobranca.Anual && <span className="ml-1 text-2xs">2 meses grátis</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {planos.map((p) => {
              const ehAtual = p.codigo === atual.codigo && (assinatura.valor === 0 || assinatura.ciclo === ciclo)
              const preco = ciclo === CicloCobranca.Anual ? p.precoAnual : p.precoMensal
              return (
                <Card key={p.id} className={cn(ehAtual && 'ring-1 ring-primary')}>
                  <CardBody className="flex h-full flex-col">
                    <div className="flex items-center justify-between">
                      <h5 className="text-15 font-semibold text-white">{p.nome}</h5>
                      {ehAtual && <Badge variant="primary">Plano atual</Badge>}
                    </div>
                    <p className="mt-1 min-h-[40px] text-13 text-muted">{p.descricao}</p>

                    <p className="mt-3">
                      <span className="text-2xl font-semibold text-white">{preco > 0 ? formatMoney(preco) : 'Grátis'}</span>
                      {preco > 0 && <span className="text-13 text-muted">/{ciclo === CicloCobranca.Anual ? 'ano' : 'mês'}</span>}
                    </p>

                    <ul className="mt-4 flex-1 space-y-2">
                      <ItemDoPlano ok>{p.maxContas ? `${p.maxContas} contas` : 'Contas ilimitadas'}</ItemDoPlano>
                      <ItemDoPlano ok>{p.maxCartoes ? `${p.maxCartoes} cartão` : 'Cartões ilimitados'}</ItemDoPlano>
                      <ItemDoPlano ok={p.permiteRelatoriosAvancados}>Relatórios completos</ItemDoPlano>
                      <ItemDoPlano ok={p.permiteExportacao}>Exportar CSV</ItemDoPlano>
                      <ItemDoPlano ok={p.permiteNotasFiscais}>Notas fiscais por QR Code</ItemDoPlano>
                      <ItemDoPlano ok={p.permiteOpenFinance}>Open Finance</ItemDoPlano>
                      <ItemDoPlano ok={p.permiteCompartilhamento}>
                        {p.maxCompartilhamentos ? `Compartilhar com ${p.maxCompartilhamentos} ${p.maxCompartilhamentos > 1 ? 'pessoas' : 'pessoa'}` : 'Compartilhar conta'}
                      </ItemDoPlano>
                    </ul>

                    <Button
                      variant={ehAtual ? 'light' : 'primary'}
                      className="mt-5 w-full"
                      disabled={ehAtual}
                      onClick={() => setConfirmar(p)}
                    >
                      {ehAtual ? 'Você está aqui' : preco > 0 ? 'Assinar' : 'Voltar para o grátis'}
                    </Button>
                  </CardBody>
                </Card>
              )
            })}
          </div>
        </div>

        <Card>
          <CardHeader title="Pagamentos" icon="ri-receipt-line" />
          {pagamentos.length === 0 ? (
            <CardBody><p className="py-6 text-center text-13 text-muted">Nenhuma cobrança até agora.</p></CardBody>
          ) : (
            <Table>
              <THead>
                <tr><Th>Vencimento</Th><Th>Valor</Th><Th>Situação</Th><Th>Pago em</Th><Th className="text-right">Ações</Th></tr>
              </THead>
              <tbody>
                {pagamentos.map((p) => (
                  <Tr key={p.id}>
                    <Td className="text-muted">
                      {formatDate(p.vencimento)}
                      {p.planoDestino && (
                        <p className="text-2xs">Troca para {p.planoDestino.nome}{p.tipoTroca === 'Ciclo' ? ' (ciclo)' : p.tipoTroca === 'Upgrade' ? ' (upgrade)' : ''}</p>
                      )}
                    </Td>
                    <Td className="font-medium text-white">{formatMoney(p.valor)}</Td>
                    <Td>
                      <Badge variant={PAGAMENTO_VARIANT[p.status] ?? 'muted'}>{p.canceladoEm ? 'Cancelada' : STATUS_PAGAMENTO_LABEL[p.status]}</Badge>
                      {p.canceladoMotivo && <p className="mt-1 max-w-[220px] truncate text-2xs text-muted" title={p.canceladoMotivo}>{p.canceladoMotivo}</p>}
                    </Td>
                    <Td className="text-muted">{p.pagoEm ? formatDate(p.pagoEm) : '—'}</Td>
                    <Td className="text-right">
                      {p.status === StatusPagamento.Pendente && !p.canceladoEm && (
                        <IconButton icon="ri-external-link-line" label="Pagar" tone="primary" onClick={() => gerarLink.mutate(p.id)} />
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {historico.length > 0 && (
          <Card>
            <CardHeader title="Histórico de trocas" icon="ri-history-line" />
            <Table>
              <THead>
                <tr><Th>Data</Th><Th>Troca</Th><Th>Tipo</Th><Th>Crédito</Th><Th>Cobrado</Th></tr>
              </THead>
              <tbody>
                {historico.map((h) => (
                  <Tr key={h.id}>
                    <Td className="whitespace-nowrap text-muted">{formatDateTime(h.data)}</Td>
                    <Td className="text-white">
                      {h.planoOrigem?.nome ?? '—'} → {h.planoDestino?.nome ?? '—'}
                    </Td>
                    <Td className="text-muted">{TIPO_HISTORICO_LABEL[h.tipo] ?? h.tipo}</Td>
                    <Td className="whitespace-nowrap text-muted">{h.credito ? formatMoney(h.credito) : '—'}</Td>
                    <Td className="whitespace-nowrap text-muted">{h.valorCobrado ? formatMoney(h.valorCobrado) : '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
      </div>

      <Modal
        open={!!confirmar}
        onClose={() => setConfirmar(null)}
        title={`Mudar para ${confirmar?.nome ?? ''}`}
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setConfirmar(null)}>Cancelar</Button>
            <Button
              icon="ri-check-line"
              loading={assinar.isPending}
              disabled={!previa.data}
              onClick={() => confirmar && assinar.mutate(confirmar)}
            >
              Confirmar
            </Button>
          </>
        }
      >
        {confirmar && (
          <div className="space-y-3 text-13 text-muted">
            {previa.isLoading && <LoadingState />}
            {previa.isError && <p className="text-expense">{(previa.error as Error).message || 'Não foi possível calcular a troca.'}</p>}
            {previa.data && <ResumoTroca previa={previa.data} />}
          </div>
        )}
      </Modal>

      <Modal
        open={cancelarAberto}
        onClose={() => setCancelarAberto(false)}
        title="Cancelar assinatura"
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setCancelarAberto(false)}>Continuar assinante</Button>
            <Button variant="danger" loading={cancelar.isPending} onClick={() => cancelar.mutate()}>Cancelar assinatura</Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-13 text-muted">Sua conta volta para o plano grátis e os dados continuam salvos.</p>
          <Select label="Por que está cancelando?" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
            {MOTIVOS.map((m) => <option key={m} value={m}>{m}</option>)}
          </Select>
          <Input label="Quer detalhar? (opcional)" value={detalhe} onChange={(e) => setDetalhe(e.target.value)} placeholder="Conte o que faltou" />
        </div>
      </Modal>
    </>
  )
}

function ResumoTroca({ previa }: { previa: PreviaTroca }) {
  const agendado = previa.tipo === 'DowngradeAgendado'
  const dataAgendada = previa.vigenteApartirDe ?? previa.renovaEm
  return (
    <>
      {agendado ? (
        <p>
          Você passará para <strong className="text-white">{previa.planoNovo.nome}</strong>
          {dataAgendada && <> em <strong className="text-white">{formatDate(dataAgendada)}</strong></>}.
          Sem cobrança agora; até lá você segue no {previa.planoAtual.nome}.
        </p>
      ) : (
        <p>
          {previa.planoAtual.nome} → <strong className="text-white">{previa.planoNovo.nome}</strong>{' '}
          ({CICLO_LABEL[previa.ciclo]}, {formatMoney(previa.valorCheioNovo)}).
        </p>
      )}
      <dl className="space-y-1.5 rounded bg-surface2 p-3">
        <Linha rotulo="Tipo" valor={previa.tipo === 'Upgrade' ? 'Upgrade' : previa.tipo === 'Ciclo' ? 'Troca de ciclo' : 'Downgrade agendado'} />
        {previa.credito > 0 && <Linha rotulo="Crédito dos dias não usados" valor={formatMoney(previa.credito)} />}
        <Linha rotulo="Cobrado agora" valor={previa.cobraAgora ? formatMoney(previa.valorACobrar) : formatMoney(0)} forte />
        {previa.diasDoCiclo > 0 && <Linha rotulo="Dias restantes" valor={`${previa.diasRestantes} de ${previa.diasDoCiclo}`} />}
        {previa.renovaEm && <Linha rotulo="Renova em" valor={formatDate(previa.renovaEm)} />}
        {!agendado && (
          <Linha rotulo="Vale a partir de" valor={previa.vigenteApartirDe ? formatDate(previa.vigenteApartirDe) : 'Ao confirmar o pagamento'} />
        )}
      </dl>
      {previa.mensagem && <p>{previa.mensagem}</p>}
    </>
  )
}

function Linha({ rotulo, valor, forte }: { rotulo: string; valor: string; forte?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt>{rotulo}</dt>
      <dd className={forte ? 'font-semibold text-white' : 'text-white'}>{valor}</dd>
    </div>
  )
}

function LinhaDeUso({ titulo, usado, limite }: { titulo: string; usado: number; limite?: number }) {
  const percentual = limite ? Math.min((usado / limite) * 100, 100) : 0
  const tone = !limite ? 'income' : percentual >= 100 ? 'expense' : percentual >= 80 ? 'warning' : 'primary'

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-13">
        <span className="text-muted">{titulo}</span>
        <span className="text-white">{usado}{limite ? ` de ${limite}` : ' · sem limite'}</span>
      </div>
      <ProgressBar value={limite ? percentual : 100} tone={tone} />
    </div>
  )
}

function ItemDoPlano({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li className={cn('flex items-start gap-2 text-13', ok ? 'text-white' : 'text-muted/60 line-through')}>
      <i className={cn('mt-0.5', ok ? 'ri-check-line text-income' : 'ri-close-line text-muted')} aria-hidden />
      <span>{children}</span>
    </li>
  )
}

export type { UsoDoPlano }
